import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill, type StatusTone } from '@/components/px/StatusPill'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { epochLabel } from '@/features/projects/format'
import { selectSessionPrincipalKey } from '@/features/auth/slices/sessionSlice'
import { selectCanWriteProject, selectProjectMembers } from '@/features/projects/slices/projectDetailSlice'
import { useAppDispatch, useAppSelector } from '@/store'
import { callerSide } from '../callerSide'
import {
  executeRollback,
  loadRollback,
  selectMigrationMutating,
  selectMigrationNonce,
  selectRollbackError,
  selectRollbackLoading,
  selectRollbackState,
  toDisplayError
} from '../slices/migrationSlice'
import { ROLLBACK_REASON_MIN_LENGTH, type RollbackStatus } from '../types'

const ROLLBACK_TONE: Record<RollbackStatus, StatusTone> = {
  NOT_ARMED: 'neutral',
  ARMED: 'review',
  EXECUTING: 'proposed',
  ROLLED_BACK: 'blocked',
  RELEASED: 'done'
}

/**
 * The rollback panel (AC-6; Decisions 4–6) — self-contained. `goLiveInvalidated`
 * is READ from the field, never inferred (INV-5.5). Execute is offered only
 * to a FIRM-side caller on the project's write roster while the last-known
 * status is ARMED — the server's own refusal is still what gates the call
 * and is rendered inline unchanged (INV: hide, never decide). There is no
 * release action: no `/rollback/release` route exists (Decision 5).
 */
export function RollbackPanel({
  orgId,
  projectId,
  planId,
  runId
}: {
  orgId: string
  projectId: string
  planId: string | null
  runId: string | null
}) {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  const rollback = useAppSelector(selectRollbackState)
  const loading = useAppSelector(selectRollbackLoading)
  const error = useAppSelector(selectRollbackError)
  const mutating = useAppSelector(selectMigrationMutating)
  const nonce = useAppSelector(selectMigrationNonce)
  const canWrite = useAppSelector(selectCanWriteProject)
  const members = useAppSelector(selectProjectMembers)
  const principalKey = useAppSelector(selectSessionPrincipalKey)

  // Fail closed (finding 6): a caller with no roster row is on NEITHER
  // side and is offered neither act — unlike the old `!== 'CLIENT'` gate,
  // which read an unknown caller as FIRM and offered Execute.
  const isFirmSide = useMemo(() => callerSide(members, principalKey) === 'FIRM', [members, principalKey])

  const [showForm, setShowForm] = useState(false)
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (planId === null || runId === null) return
    void dispatch(loadRollback({ orgId, projectId, planId, runId }))
  }, [dispatch, orgId, projectId, planId, runId, nonce])

  const onRetryClicked = useCallback(() => {
    if (planId === null || runId === null) return
    void dispatch(loadRollback({ orgId, projectId, planId, runId }))
  }, [dispatch, orgId, projectId, planId, runId])

  const onSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      if (planId === null || runId === null) return
      void dispatch(executeRollback({ orgId, projectId, planId, runId, input: { reason } })).then((result) => {
        if (executeRollback.fulfilled.match(result)) {
          setShowForm(false)
          setReason('')
        }
      })
    },
    [dispatch, orgId, projectId, planId, runId, reason]
  )

  if (planId === null || runId === null) {
    return (
      <section aria-label={t('migration.rollback.title')} className="rounded-lg border border-border bg-card px-4 py-3.5">
        <SectionLabel>{t('migration.rollback.title')}</SectionLabel>
        <div className="mt-2">
          <EmptyPanel title={t('migration.rollback.noRunTitle')} body={t('migration.rollback.noRunBody')} />
        </div>
      </section>
    )
  }

  const canOfferExecute = canWrite && isFirmSide && rollback?.status === 'ARMED'
  const reasonValid = reason.trim().length >= ROLLBACK_REASON_MIN_LENGTH

  return (
    <section
      aria-label={t('migration.rollback.title')}
      className="rounded-lg border border-border bg-card px-4 py-3.5"
      data-testid="migration-rollback-panel"
    >
      <SectionLabel>{t('migration.rollback.title')}</SectionLabel>

      {error !== null && rollback === null ? (
        <div className="mt-2">
          <ErrorPanel error={toDisplayError(error)} onRetryClicked={onRetryClicked} />
        </div>
      ) : loading && rollback === null ? (
        <div className="mt-2">
          <LoadingPanel label={t('migration.rollback.loading')} />
        </div>
      ) : rollback === null ? (
        <div className="mt-2">
          <EmptyPanel title={t('migration.rollback.emptyTitle')} body={t('migration.rollback.emptyBody')} />
        </div>
      ) : (
        <div className="mt-2 rounded-lg border border-border px-4 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill tone={ROLLBACK_TONE[rollback.status]}>{t(`migration.rollbackStatus.${rollback.status}`)}</StatusPill>
            <span className="text-xs text-muted-foreground">
              {rollback.open ? t('migration.rollback.windowOpen') : t('migration.rollback.windowClosed')}
            </span>
          </div>

          {rollback.goLiveInvalidated && (
            <p className="mt-2 rounded-md bg-blocked-bg px-3 py-2 text-sm font-semibold text-blocked">
              {t('migration.rollback.goLiveInvalidated')}
            </p>
          )}

          <dl className="mt-2 space-y-0.5 text-xs text-muted-foreground">
            {rollback.armedAt !== undefined && (
              <div>{t('migration.rollback.armedAt', { at: epochLabel(rollback.armedAt) })}</div>
            )}
            {rollback.executedAt !== undefined && (
              <div>
                {t('migration.rollback.executedAt', {
                  at: epochLabel(rollback.executedAt),
                  name: rollback.executedByUserName ?? '—'
                })}
              </div>
            )}
            {rollback.executeReason !== undefined && (
              <div>{t('migration.rollback.executeReason', { reason: rollback.executeReason })}</div>
            )}
            {rollback.rolledBackAt !== undefined && (
              <div>{t('migration.rollback.rolledBackAt', { at: epochLabel(rollback.rolledBackAt) })}</div>
            )}
            {rollback.releasedAt !== undefined && (
              <div>{t('migration.rollback.releasedAt', { at: epochLabel(rollback.releasedAt) })}</div>
            )}
            <div>
              {rollback.rollbackPathTested
                ? t('migration.rollback.pathTested')
                : t('migration.rollback.pathNotTested')}
            </div>
            <div>
              {t('migration.rollback.reminderWindow', { days: rollback.reminderWindowDays })}
              {rollback.reminderDue && ` · ${t('migration.rollback.reminderDue')}`}
            </div>
          </dl>

          {error !== null && (
            <p role="alert" data-testid="rollback-execute-error" className="mt-2 text-xs text-blocked">
              {error.message}
            </p>
          )}

          {canOfferExecute && (
            <div className="mt-3">
              {!showForm ? (
                <Button type="button" variant="outline" onClick={() => setShowForm(true)}>
                  {t('migration.rollback.executeAction')}
                </Button>
              ) : (
                <form onSubmit={onSubmit} className="space-y-2 rounded-lg border border-border bg-muted/30 p-4">
                  <div>
                    <label htmlFor="rollback-reason" className="text-xs font-medium text-foreground">
                      {t('migration.rollback.form.reason')}
                    </label>
                    <Input
                      id="rollback-reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      required
                      minLength={ROLLBACK_REASON_MIN_LENGTH}
                      className="mt-1"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">{t('migration.rollback.form.note')}</p>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={!reasonValid || mutating === 'rollback-execute'}>
                      {t('migration.rollback.form.submit')}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                      {t('migration.rollback.form.cancel')}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  )
}
